<template>
  <SetupBulkUpload
    v-model="dialog"
    show-trigger
    heading="Bulk Upload"
    :test-ids="shellTestIds"
    :step1-description="step1Description"
    :step2-description="step2Description"
    :has-preview="hasUploadPreview"
    :validating="isFileProcessing"
    :file-name="selectedFileName"
    :reupload-visible="hasUploadPreview || isFileProcessing || uploadBlockingErrors"
    :file="filesbulk"
    :reset-dropzone="resetTrigger"
    :show-inline-errors="uploadBlockingErrors"
    :inline-error-text="bulkUploadErrorFooterText"
    :header-error-messages="headerErrorMessages"
    :data-error-messages="inlineDataErrorMessages"
    :footer-visible="showBulkErrorFooter || hasFileDataError || dataOnlyWithError"
    :error-footer-visible="showBulkErrorFooter"
    :error-footer-text="bulkUploadErrorFooterText"
    :footer-summary="dataErrorMessages"
    :error-filter-visible="hasFileDataError || dataOnlyWithError"
    :show-errors-only="dataOnlyWithError"
    :busy="loading"
    :can-submit="canSubmitUpload"
    @open="onShellOpen"
    @cancel="onShellCancel"
    @download="downloadTemplate"
    @files-change="updateFilesBulk"
    @reupload="reuploadDoc"
    @upload="uploadData"
    @update:showErrorsOnly="dataOnlyWithError = $event"
  >
    <template #preview>
      <div
        class="coa-bulk-upload__table-panel"
        data-testid="financialclose_component_chartofaccount_bulkupload__table-panel"
      >
        <div class="coa-bulk-upload__table-scroll bulk-import-dialog__legacy-table">
          <v-simple-table
            dense
            class="radius-small border-on simple-table"
            data-testid="financialclose_component_chartofaccount_bulkupload__table"
          >
            <template v-slot:default>
              <thead
                class="tableHeaderBg"
                data-testid="financialclose_component_chartofaccount_bulkupload__table-header"
              >
                <th
                  v-for="(item, index) in tableheader"
                  :key="index"
                  class="text-left pa-2 border-right-data tableHeaderBg"
                  :scope="item.value"
                  :data-testid="`financialclose_component_chartofaccount_bulkupload__table-header-col-${item.value}`"
                >
                  {{ item.name }}
                </th>
              </thead>
              <tbody>
                <tr
                  v-for="item in filteredPreviewRows"
                  :key="'row-' + item.index"
                  :data-testid="`financialclose_component_chartofaccount_bulkupload__table-row-${item.index}`"
                >
                  <td
                    v-for="header in tableheader"
                    :key="'col-' + header.value + '-' + item.index"
                    class="font-size-xm border-right-data px-2"
                    :class="previewCellClass(item.index, header)"
                    :data-testid="cellTestId(item.index, header.value)"
                  >
                    <v-tooltip
                      v-if="cellError(item.index, header.value)"
                      bottom
                      content-class="tooltip-bottom"
                    >
                      <template #activator="{ on, attrs }">
                        <div
                          v-bind="attrs"
                          v-on="on"
                          class="bulk-import-dialog__cell-inner bulk-import-dialog__cell-inner--error error--text"
                        >
                          <span class="bulk-import-dialog__cell--truncate">
                            {{ cellError(item.index, header.value) }}
                          </span>
                        </div>
                      </template>
                      <span>{{ cellError(item.index, header.value) }}</span>
                    </v-tooltip>

                    <div v-else-if="header.value === 'sno'">
                      {{ item.index + 1 }}
                    </div>

                    <div
                      v-else-if="header.value === 'checklistref'"
                      class="d-flex flex-wrap pa-2"
                      style="gap: 4px;"
                    >
                      <span
                        v-for="(va, ka) in item.row.checklistref"
                        :key="ka"
                        class="caccounting-background caccounting--text pa-1 px-2 radius-mini text-no-wrap"
                        :data-testid="`financialclose_component_chartofaccount_bulkupload__chip-${ka}`"
                      >{{ va }}</span>
                    </div>

                    <div
                      v-else-if="header.value === 'location'"
                      class="d-flex flex-wrap pa-2"
                      style="gap: 4px;"
                    >
                      <span
                        v-for="(va, ka) in normalizeCommaSeparatedField(item.row.location).split(',').filter(Boolean)"
                        :key="ka"
                        class="caccounting-background caccounting--text pa-1 px-2 radius-mini text-no-wrap"
                        :data-testid="`financialclose_component_chartofaccount_bulkupload__location-chip-${ka}`"
                      >{{ va }}</span>
                    </div>

                    <div v-else>{{ item.row[header.value] }}</div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </div>
    </template>
  </SetupBulkUpload>
</template>

<script>
import { mapActions } from 'vuex';
import { SetupBulkUpload } from '@/modules/financialclose/components';
import { ERROR_MESSAGES } from '@/modules/financialclose/utils/constants';
import './BulkUploadChartOfAccount.scss';

const EXPECTED_HEADERS = [
  'Account No',
  'Name',
  'Type',
  'Category',
  'Sub Category',
  'Tasklist Reference',
  'Location',
];

const ACCOUNT_TYPES = ['Asset', 'Equity', 'Liability', 'Income', 'Expense', 'Others'];

const SHELL_TEST_IDS = {
  root: 'financialclose_component_chartofaccount_bulkupload__root',
  openButton: 'financialclose_component_chartofaccount_bulkupload__open-drawer-btn',
  drawer: 'financialclose_component_chartofaccount_bulkupload__drawer',
  reuploadInput: 'financialclose_component_chartofaccount_bulkupload__reupload-input',
  body: 'financialclose_component_chartofaccount_bulkupload__drawer-body',
  step1: 'financialclose_component_chartofaccount_bulkupload__step-1',
  download: 'financialclose_component_chartofaccount_bulkupload__download-template-btn',
  step2: 'financialclose_component_chartofaccount_bulkupload__step-2',
  reuploadButton: 'financialclose_component_chartofaccount_bulkupload__reupload-btn',
  validating: 'financialclose_component_chartofaccount_bulkupload__validating',
  inlineErrors: 'financialclose_component_chartofaccount_bulkupload__inline-errors',
  inlineErrorMessage: 'financialclose_component_chartofaccount_bulkupload__inline-error-msg',
  headerErrors: 'financialclose_component_chartofaccount_bulkupload__header-errors',
  headerErrorsList: 'financialclose_component_chartofaccount_bulkupload__header-errors-list',
  headerError: 'financialclose_component_chartofaccount_bulkupload__header-error',
  rowErrors: 'financialclose_component_chartofaccount_bulkupload__data-errors',
  dataError: 'financialclose_component_chartofaccount_bulkupload__data-error',
  dropzone: 'financialclose_component_chartofaccount_bulkupload__dragdrop-panel',
  dragDrop: 'financialclose_component_chartofaccount_bulkupload__dragdrop-component',
  preview: 'financialclose_component_chartofaccount_bulkupload__preview',
  errorFilterRow: 'financialclose_component_chartofaccount_bulkupload__show-errors-checkbox-row',
  errorFilter: 'financialclose_component_chartofaccount_bulkupload__show-errors-checkbox',
  errorFooter: 'financialclose_component_chartofaccount_bulkupload__error-msg',
  errorFooterMessage: 'financialclose_component_chartofaccount_bulkupload__error-msg',
  summaryErrors: 'financialclose_component_chartofaccount_bulkupload__preview-data-errors',
  cancel: 'financialclose_component_chartofaccount_bulkupload__cancel-btn',
  upload: 'financialclose_component_chartofaccount_bulkupload__upload-btn',
};

export default {
  name: 'financialclose_component_chartofaccount_bulkupload',
  components: {
    SetupBulkUpload,
  },
  data() {
    return {
      shellTestIds: SHELL_TEST_IDS,
      step1Description: 'Start by downloading the Chart of Accounts Excel template.',
      step2Description: 'Fill in the Excel file, then drag and drop it here to upload chart of accounts.',
      right: 'financialclose_master_chartofaccount',
      file: null,
      fileprocessing: {},
      errorflag: false,
      uploaddata: [],
      dataerror: [],
      dialog: false,
      loading: false,
      filesbulk: [],
      resetTrigger: false,
      dataOnlyWithError: false,
      parseGeneration: 0,
      headerErrorMessages: [],
      dataErrorMessages: [],
      tableheader: [
        { name: 'S No', value: 'sno' },
        { name: 'Account No.', value: 'glcode' },
        { name: 'Account Name', value: 'name' },
        { name: 'Type', value: 'type' },
        { name: 'Category', value: 'category' },
        { name: 'Sub Category', value: 'subcategory' },
        { name: 'Tasks Linked', value: 'checklistref' },
        { name: 'Location', value: 'location' },
      ],
    };
  },
  computed: {
    hasUploadPreview() {
      return (this.uploaddata || []).length > 0;
    },
    isFileProcessing() {
      return this.loading && !this.hasUploadPreview;
    },
    selectedFileName() {
      const current = Array.isArray(this.filesbulk) ? this.filesbulk[0] : this.filesbulk;
      return current?.name || '';
    },
    hasFileDataError() {
      return (this.dataerror || []).some((row) => row && Object.keys(row).length > 0);
    },
    uploadBlockingErrors() {
      return !this.hasUploadPreview && (this.headerErrorMessages.length > 0 || this.dataErrorMessages.length > 0);
    },
    inlineDataErrorMessages() {
      return this.uploadBlockingErrors ? this.dataErrorMessages : [];
    },
    showBulkErrorFooter() {
      return this.hasUploadPreview && this.errorflag;
    },
    canSubmitUpload() {
      return this.hasUploadPreview && !this.errorflag && !this.headerErrorMessages.length;
    },
    bulkUploadErrorFooterText() {
      return ERROR_MESSAGES.BULK_UPLOAD_ERROR_FOOTER;
    },
    filteredPreviewRows() {
      return (this.uploaddata || [])
        .map((row, index) => ({
          row,
          index,
          errors: this.dataerror[index] || {},
        }))
        .filter((item) => {
          if (!this.dataOnlyWithError) return true;
          return Object.keys(item.errors).length > 0;
        });
    },
  },
  beforeDestroy() {
    this.resetData();
  },
  methods: {
    ...mapActions('chartOfAccount', ['bulkAddChartOfAccounts']),
    onShellOpen() {
      this.resetData();
      this.dialog = true;
    },
    onShellCancel() {
      this.dialog = false;
      this.resetData();
    },
    downloadTemplate() {
      this.$nova.downloadFile('', {}, '/v2/financialclose/masters/chartofaccount/getbulksubmittemplate');
    },
    cellError(index, field) {
      if (field === 'sno') return '';
      return (this.dataerror[index] && this.dataerror[index][field]) || '';
    },
    previewCellClass(index, header) {
      if (this.cellError(index, header.value)) {
        return ['bulk-import-dialog__cell--error'];
      }
      return [];
    },
    cellTestId(index, field) {
      const base = `financialclose_component_chartofaccount_bulkupload__table-cell-${field}`;
      if (field === 'sno') {
        return 'financialclose_component_chartofaccount_bulkupload__table-row-serial';
      }
      return this.cellError(index, field) ? `${base}-error` : base;
    },
    recordFieldError(errors, messages, rowNumber, field, message) {
      errors[field] = message;
      messages.push(`Row ${rowNumber}: ${message}`);
    },
    normalizeCommaSeparatedField(value) {
      return String(value || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .join(',');
    },
    readFile(key) {
      const file = this.filesbulk[0];
      if ((file || {}).name && (this.fileprocessing[key] || 0) === 0) {
        const possibletypeslower = ACCOUNT_TYPES.map((type) => type.toLowerCase());
        this.loading = true;
        this.fileprocessing[key] = 1;
        this.parseGeneration += 1;
        const parseId = this.parseGeneration;
        const ext = ((file || {}).name || '').split('.').pop();
        Promise.resolve()
          .then(() => {
            if (['xlsx', 'xls', 'xlsm', 'xlsb'].indexOf(ext) === -1) {
              throw new Error('Only xlsx,xls,xlsm,xlsb file extension supported');
            }
            return this.$nova.readExcelFile(file, 'TOP SHEET', false);
          })
          .then((dt) => {
            if (parseId !== this.parseGeneration) return;
            const actualHeaders = dt[0] || [];
            if (JSON.stringify(actualHeaders) === JSON.stringify(EXPECTED_HEADERS)) {
              const filedata = [];
              const errordata = [];
              const validationErrors = [];
              this.errorflag = false;
              this.headerErrorMessages = [];
              this.dataErrorMessages = [];
              for (let i = 0; i < dt.length; i++) {
                if (i > 0) {
                  const el = dt[i];
                  const rowNumber = i;
                  const rowdata = {
                    glcode: el[0],
                    name: el[1],
                    type: el[2],
                    category: el[3],
                    subcategory: el[4],
                    checklistref: (el[5] || '').split(',').filter((part) => part.replaceAll(/\s/g, '')),
                    location: this.normalizeCommaSeparatedField(el[6]),
                  };
                  const errors = {};
                  const typeValue = (rowdata.type || '').toString().trim();
                  if (!typeValue) {
                    this.recordFieldError(errors, validationErrors, rowNumber, 'type', ERROR_MESSAGES.TYPE_REQUIRED);
                  } else {
                    const typeindex = possibletypeslower.indexOf(typeValue.toLowerCase());
                    if (typeindex > -1) rowdata.type = ACCOUNT_TYPES[typeindex];
                    else {
                      this.recordFieldError(
                        errors,
                        validationErrors,
                        rowNumber,
                        'type',
                        ERROR_MESSAGES.COA_TYPE_INVALID,
                      );
                    }
                  }
                  if ((rowdata.glcode || '').toString().trim() === '') {
                    this.recordFieldError(
                      errors,
                      validationErrors,
                      rowNumber,
                      'glcode',
                      ERROR_MESSAGES.COA_ACCOUNT_NO_REQUIRED,
                    );
                  }
                  if ((rowdata.name || '').toString().trim() === '') {
                    this.recordFieldError(
                      errors,
                      validationErrors,
                      rowNumber,
                      'name',
                      ERROR_MESSAGES.COA_ACCOUNT_NAME_REQUIRED,
                    );
                  }
                  if ((rowdata.category || '').toString().trim() === '') {
                    this.recordFieldError(
                      errors,
                      validationErrors,
                      rowNumber,
                      'category',
                      ERROR_MESSAGES.COA_CATEGORY_REQUIRED,
                    );
                  }
                  if ((rowdata.subcategory || '').toString().trim() === '') {
                    this.recordFieldError(
                      errors,
                      validationErrors,
                      rowNumber,
                      'subcategory',
                      ERROR_MESSAGES.COA_SUB_CATEGORY_REQUIRED,
                    );
                  }
                  if (Object.keys(errors).length > 0) this.errorflag = true;
                  filedata.push(rowdata);
                  errordata.push(errors);
                }
              }
              this.dataErrorMessages = validationErrors;
              this.uploaddata = filedata;
              this.dataerror = errordata;
              this.dataOnlyWithError = false;
            } else {
              this.headerErrorMessages = [
                'Column headers mismatch detected:',
                `Expected headers: '${EXPECTED_HEADERS.join("', '")}'`,
                `Found headers: '${actualHeaders.join("', '")}'`,
                'Please ensure columns are in the correct order.',
              ];
              this.errorflag = true;
              this.uploaddata = [];
              this.dataerror = [];
              this.dataErrorMessages = [];
              this.$store.commit('sbError', ERROR_MESSAGES.COA_HEADER_MISMATCH);
            }
          })
          .catch((err) => {
            if (parseId !== this.parseGeneration) return;
            console.log('[BulkUploadChartOfAccount]:', err);
            const message = err.message || err.toString() || ERROR_MESSAGES.UNKNOWN_ERROR;
            this.dataErrorMessages = [message];
            this.headerErrorMessages = [];
            this.errorflag = true;
            this.uploaddata = [];
            this.dataerror = [];
            this.filesbulk = [];
            this.$store.commit('sbError', message);
          })
          .finally(() => {
            if (parseId !== this.parseGeneration) return;
            this.file = null;
            this.fileprocessing[key] = 0;
            this.loading = false;
          });
      }
    },
    applyBulkServerError(result) {
      const message = (result && result.message) || ERROR_MESSAGES.ERROR_UPLOADING_CHART_OF_ACCOUNT;
      const rowErrors = result && result.rowErrors;
      if (rowErrors) {
        const dataerror = (this.dataerror || []).slice();
        const messages = [];
        Object.keys(rowErrors).forEach((key) => {
          const rowIndex = Number(key);
          const fields = rowErrors[key] || {};
          dataerror[rowIndex] = { ...(dataerror[rowIndex] || {}), ...fields };
          Object.keys(fields).forEach((field) => {
            messages.push(`Row ${rowIndex + 1}: ${fields[field]}`);
          });
        });
        this.dataerror = dataerror;
        this.dataErrorMessages = messages.length ? messages : [message];
        this.errorflag = true;
        return;
      }
      this.dataErrorMessages = [message];
      this.errorflag = true;
      this.$store.commit('sbError', message);
    },
    uploadData() {
      if (!this.canSubmitUpload) {
        this.$store.commit('sbError', this.bulkUploadErrorFooterText);
        return;
      }
      this.loading = true;
      this.bulkAddChartOfAccounts(this.uploaddata)
        .then((result) => {
          if (result && result.ok) {
            this.dialog = false;
            this.resetData();
            this.$emit('reload');
            this.$nova.gotoLink({ path: 'tasklist' });
            return;
          }
          this.applyBulkServerError(result);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateFilesBulk(newFilesBulk) {
      this.filesbulk = newFilesBulk;
      this.readFile('file');
    },
    resetDragDrop() {
      this.resetTrigger = false;
      this.$nextTick(() => {
        this.resetTrigger = true;
      });
    },
    reuploadDoc() {
      this.resetUploadState();
    },
    resetUploadState() {
      this.parseGeneration += 1;
      this.fileprocessing = {};
      this.loading = false;
      this.uploaddata = [];
      this.dataerror = [];
      this.errorflag = false;
      this.headerErrorMessages = [];
      this.dataErrorMessages = [];
      this.dataOnlyWithError = false;
      this.filesbulk = [];
      this.file = null;
      this.resetDragDrop();
    },
    resetData() {
      this.resetUploadState();
    },
  },
};
</script>
